import { useState, useEffect, useMemo, memo, FC } from "react";
import {
  Chart,
  CommonSeriesSettings,
  Series,
  ValueAxis,
  ArgumentAxis,
  Label,
  Export,
  Format,
  Grid,
  Tooltip,
  Legend,
  Size,
  Title,
} from "devextreme-react/chart";
import { SelectBox } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import { useFetch } from "src/hooks/useFetch";
import { SpatialRun } from "../types";
import { tileLayoutManager, WellCount } from "./TileLayoutManager";
import { useRunTileLabels } from "./use-run-tile-labels";

// ============ API response types ============

interface Failure {
  Code: string;
  Message: string;
}

interface CellPaintMetricStats {
  Name: string;
  Value: number;
  TileValues: number[];
}

interface TargetCellPaintStats {
  Target: string;
  AnalysisSetting: string;
  CellPaintMetricStats: CellPaintMetricStats[];
}

interface WellCellPaintStats {
  WellLocation: string;
  Failure?: Failure | null;
  TargetCellPaintStats: TargetCellPaintStats[];
}

interface AnalysisSpatialCellPaintStats {
  BatchName: string;
  WellCellPaintStats: WellCellPaintStats[];
  RunTargetProjectionRegistrationSuccessRates?: Record<string, number>;
}

type CellPaintResponse = Record<string, AnalysisSpatialCellPaintStats>;

// ============ Constants ============

const SENTINEL = -999;

// Sequential pastel ramp (low -> high)
const heatmapRamp = [
  "#f3e5f5",
  "#e1bee7",
  "#ce93d8",
  "#ba68c8",
  "#ab47bc",
  "#8e24aa",
  "#6a1b9a",
];

const GAP_COLOR = "#e0e0e0";

const isValid = (v: number | undefined | null): v is number =>
  typeof v === "number" && !isNaN(v) && v !== SENTINEL;

const colorForValue = (value: number, min: number, max: number): string => {
  if (!isValid(value)) return GAP_COLOR;
  if (max <= min) return heatmapRamp[Math.floor(heatmapRamp.length / 2)];
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const idx = Math.min(
    Math.floor(t * (heatmapRamp.length - 1)),
    heatmapRamp.length - 2
  );
  const frac = t * (heatmapRamp.length - 1) - idx;
  return frac < 0.5 ? heatmapRamp[idx] : heatmapRamp[idx + 1];
};

// Add ~5% padding to a [min,max] range, anchoring zero when min >= 0
const padRange = (
  min: number,
  max: number
): { startValue: number; endValue: number } | undefined => {
  if (!isFinite(min) || !isFinite(max)) return undefined;
  if (min === max) {
    const pad = Math.abs(min) * 0.05 || 1;
    return { startValue: min - pad, endValue: max + pad };
  }
  const pad = (max - min) * 0.05;
  const startValue = min >= 0 ? Math.max(0, min - pad) : min - pad;
  return { startValue, endValue: max + pad };
};

// ============ Component ============

const CytoCellPaint: FC<{ run: SpatialRun }> = memo(({ run }) => {
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [selectedMetric, setSelectedMetric] = useState<string>("");
  const [tilePositions, setTilePositions] = useState<
    Awaited<ReturnType<typeof tileLayoutManager.loadLayout>>
  >([]);
  const [layoutLoaded, setLayoutLoaded] = useState(false);

  const {
    wellTilesByLocation,
    isLoading: isWellTileOrderLoading,
  } = useRunTileLabels(run);

  const {
    data: rawData,
    isLoading,
    isError,
    error,
  } = useFetch(`/spatialRun/${run?.runID}/cellPaint`, !!run?.runID);

  const cellPaintData = rawData as CellPaintResponse | undefined;

  // Sorted batch names from response
  const batchNames = useMemo(() => {
    if (!cellPaintData) return [];
    return Object.keys(cellPaintData).sort((a, b) => a.localeCompare(b));
  }, [cellPaintData]);

  useEffect(() => {
    if (batchNames.length > 0 && !batchNames.includes(selectedBatch)) {
      setSelectedBatch(batchNames[0]);
    }
  }, [batchNames, selectedBatch]);

  // Active batch object
  const batch = useMemo(() => {
    if (!cellPaintData || !selectedBatch) return undefined;
    return cellPaintData[selectedBatch];
  }, [cellPaintData, selectedBatch]);

  // Targets in batch (in order of first appearance across wells)
  const targets = useMemo<string[]>(() => {
    if (!batch) return [];
    const seen = new Set<string>();
    const ordered: string[] = [];
    batch.WellCellPaintStats.forEach((w) => {
      w.TargetCellPaintStats?.forEach((t) => {
        if (!seen.has(t.Target)) {
          seen.add(t.Target);
          ordered.push(t.Target);
        }
      });
    });
    return ordered;
  }, [batch]);

  // Union of metric names in batch (sorted)
  const metricNames = useMemo<string[]>(() => {
    if (!batch) return [];
    const set = new Set<string>();
    batch.WellCellPaintStats.forEach((w) => {
      w.TargetCellPaintStats?.forEach((t) => {
        t.CellPaintMetricStats?.forEach((m) => {
          set.add(m.Name);
        });
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [batch]);

  useEffect(() => {
    if (metricNames.length === 0) return;
    if (!metricNames.includes(selectedMetric)) {
      setSelectedMetric(metricNames[0]);
    }
  }, [metricNames, selectedMetric]);

  // Detect well count from run + load tile layout
  const wellCount: WellCount = useMemo(() => {
    return tileLayoutManager.detectWellCount(run?.wellLayout || "", run?.kitType);
  }, [run?.wellLayout, run?.kitType]);

  useEffect(() => {
    let cancelled = false;
    setLayoutLoaded(false);
    tileLayoutManager.loadLayout(wellCount).then((positions) => {
      if (cancelled) return;
      setTilePositions(positions);
      setLayoutLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [wellCount]);

  // TileValues are per well, in Wells[].Tiles order. The flat Tiles list is a
  // serpentine walk and shifts those values onto the wrong tiles.
  const perWellTileOrder = wellTilesByLocation;

  useEffect(() => {
    if (isWellTileOrderLoading || wellTilesByLocation.size > 0 || !run?.runID) {
      return;
    }
    console.error(
      "Missing runParameters Wells[].Tiles. Refusing to map cell paint tile values without explicit tile ordering."
    );
  }, [isWellTileOrderLoading, wellTilesByLocation, run?.runID]);

  if (isLoading) {
    return <div style={{ padding: 20 }}>Loading Cell Paint data...</div>;
  }

  if (isError) {
    return (
      <div style={{ padding: 20 }}>
        Error loading Cell Paint data:{" "}
        {error instanceof Error ? error.message : "Unknown error"}
      </div>
    );
  }

  if (!cellPaintData || batchNames.length === 0) {
    return <div style={{ padding: 20 }}>No Cell Paint data available.</div>;
  }

  if (!batch) {
    return <div style={{ padding: 20 }}>Loading...</div>;
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        {batchNames.length > 1 && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 10 }}>Batch:</span>
            <SelectBox
              value={selectedBatch}
              items={batchNames}
              onValueChanged={(e) => setSelectedBatch(e.value)}
              width={140}
            />
          </div>
        )}
      </div>

      <CellPaintBarChart
        run={run}
        batch={batch}
        metricNames={metricNames}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
      />

      <CellPaintDensityGrid
        batch={batch}
        targets={targets}
        metricNames={metricNames}
        tilePositions={tilePositions}
        perWellTileOrder={perWellTileOrder}
        layoutLoaded={layoutLoaded && !isWellTileOrderLoading}
      />
    </div>
  );
});

// ============ Bar chart ============

interface BarChartProps {
  run: SpatialRun;
  batch: AnalysisSpatialCellPaintStats;
  metricNames: string[];
  selectedMetric: string;
  setSelectedMetric: (m: string) => void;
}

interface BarRow {
  Well: string;
  value: number | null; // null -> gap
  Target: string;
  Failure: string | null;
}

const CellPaintBarChart: FC<BarChartProps> = ({
  run,
  batch,
  metricNames,
  selectedMetric,
  setSelectedMetric,
}) => {
  // Guard against the brief render after a batch switch where `selectedMetric`
  // is still set to a value from the previous batch (the useEffect that resets
  // it runs after this render). Using the user's selection if it's valid in the
  // current batch, otherwise falling back to the first available metric.
  const effectiveMetric = metricNames.includes(selectedMetric)
    ? selectedMetric
    : metricNames[0] || "";

  const { rows, valueRange } = useMemo(() => {
    const rows: BarRow[] = [];
    let min = Infinity;
    let max = -Infinity;

    if (!effectiveMetric) {
      return { rows, valueRange: padRange(0, 1) };
    }

    const wellOrder =
      run?.wells?.map((w) => w.WellLocation) ??
      batch.WellCellPaintStats.map((w) => w.WellLocation);

    wellOrder.forEach((wellLoc) => {
      const well = batch.WellCellPaintStats.find(
        (w) => w.WellLocation === wellLoc
      );
      if (!well) {
        rows.push({
          Well: wellLoc,
          value: null,
          Target: "",
          Failure: null,
        });
        return;
      }

      if (well.Failure) {
        rows.push({
          Well: wellLoc,
          value: null,
          Target: "",
          Failure: well.Failure.Message || well.Failure.Code || "Failed",
        });
        return;
      }

      // Metric names are unique within a batch across (target, metric) pairs
      // because target is encoded in the prefix.
      let foundValue: number | null = null;
      let foundTarget = "";
      well.TargetCellPaintStats?.some((t) => {
        const m = t.CellPaintMetricStats?.find(
          (mm) => mm.Name === effectiveMetric
        );
        if (!m) return false;
        if (!isValid(m.Value)) {
          foundTarget = t.Target;
          return true;
        }
        foundValue = m.Value;
        foundTarget = t.Target;
        return true;
      });

      if (foundValue === null) {
        rows.push({
          Well: wellLoc,
          value: null,
          Target: foundTarget,
          Failure: null,
        });
      } else {
        rows.push({
          Well: wellLoc,
          value: foundValue,
          Target: foundTarget,
          Failure: null,
        });
        if (foundValue < min) min = foundValue;
        if (foundValue > max) max = foundValue;
      }
    });

    // Always return a defined range. DevExtreme's ValueAxis chokes when its
    // visualRange transitions from defined to undefined between renders.
    return {
      rows,
      valueRange: padRange(min, max) ?? { startValue: 0, endValue: 1 },
    };
  }, [batch, effectiveMetric, run?.wells]);

  // DevExtreme renders null/undefined as gaps; explicit `null` is fine.
  const dataSource = rows.map((r) => ({
    Well: r.Well,
    value: r.value,
    Target: r.Target,
    Failure: r.Failure,
  }));

  const customizeTooltip = (info: {
    argument: string;
    value: number;
    point?: { data?: BarRow };
  }) => {
    const data = info.point?.data;
    const lines = [
      `<b>Well:</b> ${info.argument}`,
      data?.Target ? `<b>Target:</b> ${data.Target}` : "",
      `<b>${effectiveMetric}:</b> ${
        info.value === null || info.value === undefined
          ? "—"
          : Number(info.value).toPrecision(4)
      }`,
      data?.Failure ? `<b>Failure:</b> ${data.Failure}` : "",
    ].filter(Boolean);
    return { text: lines.join("<br/>") };
  };

  const visibleCount = rows.filter((r) => r.value !== null).length;

  return (
    <div style={{ marginBottom: 40 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <span>Metric:</span>
        <SelectBox
          value={effectiveMetric}
          items={metricNames}
          onValueChanged={(e) => setSelectedMetric(e.value)}
          searchEnabled
          width={320}
        />
        <span style={{ color: "#999", fontSize: 12 }}>
          {visibleCount} / {rows.length} wells with data
        </span>
      </div>

      {visibleCount === 0 ? (
        <div
          style={{
            height: 380,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #ddd",
            borderRadius: 6,
            color: "#888",
            fontSize: 14,
          }}
        >
          No valid values for {effectiveMetric || "this metric"} in batch{" "}
          {batch.BatchName}.
        </div>
      ) : (
        <Chart
          // Force a clean remount when batch/metric changes so DevExtreme
          // never has to reconcile from a defined visualRange to undefined.
          key={`${batch.BatchName}::${effectiveMetric}`}
          dataSource={dataSource}
        >
          <Title
            horizontalAlignment="center"
            text={`${batch.BatchName} — ${effectiveMetric} by Well`}
          />
          <Size width="100%" height={380} />
          <CommonSeriesSettings
            argumentField="Well"
            type="bar"
            barPadding={0.2}
          />
          <Series valueField="value" name={effectiveMetric} color="#8e24aa" />
          <ValueAxis
            title={effectiveMetric}
            visualRange={[valueRange.startValue, valueRange.endValue]}
          >
            <Grid visible={true} />
          </ValueAxis>
          <ArgumentAxis title="Well">
            <Label wordWrap="none" overlappingBehavior="stagger" />
          </ArgumentAxis>
          <Legend visible={false} />
          <Export enabled />
          <Tooltip enabled customizeTooltip={customizeTooltip}>
            <Format type="fixedPoint" precision={4} />
          </Tooltip>
        </Chart>
      )}
    </div>
  );
};

// ============ Density-map grid (3 × N) ============

interface DensityGridProps {
  batch: AnalysisSpatialCellPaintStats;
  targets: string[];
  metricNames: string[];
  tilePositions: Awaited<ReturnType<typeof tileLayoutManager.loadLayout>>;
  perWellTileOrder: Map<string, string[]>;
  layoutLoaded: boolean;
}

const CellPaintDensityGrid: FC<DensityGridProps> = ({
  batch,
  targets,
  metricNames,
  tilePositions,
  perWellTileOrder,
  layoutLoaded,
}) => {
  if (!layoutLoaded) {
    return (
      <div style={{ position: "relative", minHeight: 200 }}>
        <LoadPanel
          shadingColor="rgba(0,0,0,0.2)"
          visible
          showIndicator
          shading
          message="Loading tile layout..."
        />
      </div>
    );
  }

  if (tilePositions.length === 0) {
    return (
      <div style={{ padding: 20, color: "#666" }}>
        Could not load tile layout for this run.
      </div>
    );
  }

  if (targets.length === 0 || metricNames.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: 20 }}>
      <h3 style={{ marginBottom: 16 }}>
        {batch.BatchName} — Tile Density Maps ({targets.length} targets ×{" "}
        {metricNames.length} metrics)
      </h3>
      <div style={{ overflowX: "auto", paddingBottom: 8 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `120px repeat(${metricNames.length}, 170px)`,
            gridAutoRows: "auto",
            gap: 8,
            alignItems: "stretch",
          }}
        >
          {/* Header row: empty corner + metric names */}
          <div />
          {metricNames.map((m) => (
            <div
              key={`col-${m}`}
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#333",
                textAlign: "center",
                padding: "4px 2px",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              title={m}
            >
              {m}
            </div>
          ))}

          {targets.map((target) => (
            <DensityRow
              key={target}
              target={target}
              metricNames={metricNames}
              batch={batch}
              tilePositions={tilePositions}
              perWellTileOrder={perWellTileOrder}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

interface DensityRowProps {
  target: string;
  metricNames: string[];
  batch: AnalysisSpatialCellPaintStats;
  tilePositions: Awaited<ReturnType<typeof tileLayoutManager.loadLayout>>;
  perWellTileOrder: Map<string, string[]>;
}

const DensityRow: FC<DensityRowProps> = ({
  target,
  metricNames,
  batch,
  tilePositions,
  perWellTileOrder,
}) => {
  return (
    <>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#333",
          alignSelf: "center",
          paddingRight: 8,
          textAlign: "right",
        }}
      >
        {target}
      </div>
      {metricNames.map((metric) => (
        <DensityCell
          key={`${target}-${metric}`}
          target={target}
          metric={metric}
          batch={batch}
          tilePositions={tilePositions}
          perWellTileOrder={perWellTileOrder}
        />
      ))}
    </>
  );
};

interface DensityCellProps {
  target: string;
  metric: string;
  batch: AnalysisSpatialCellPaintStats;
  tilePositions: Awaited<ReturnType<typeof tileLayoutManager.loadLayout>>;
  perWellTileOrder: Map<string, string[]>;
}

const CELL_W = 170;
const CELL_H = 230;
const CELL_PAD = 6;

const DensityCell: FC<DensityCellProps> = memo(
  ({ target, metric, batch, tilePositions, perWellTileOrder }) => {
    const [hover, setHover] = useState<{
      x: number;
      y: number;
      tileName: string;
      well: string;
      value: number;
    } | null>(null);

    const { tileValueByName, valueRange, hasData } = useMemo(() => {
      const valueMap = new Map<string, number>();
      let min = Infinity;
      let max = -Infinity;
      let anyValid = false;

      batch.WellCellPaintStats.forEach((well) => {
        if (well.Failure) return; // entire well's tiles render as gaps
        const targetEntry = well.TargetCellPaintStats?.find(
          (t) => t.Target === target
        );
        if (!targetEntry) return;
        const metricEntry = targetEntry.CellPaintMetricStats?.find(
          (m) => m.Name === metric
        );
        if (!metricEntry) return;
        const order = perWellTileOrder.get(well.WellLocation);
        if (!order) return;

        metricEntry.TileValues?.forEach((v, idx) => {
          const tileName = order[idx];
          if (!tileName) return;
          if (!isValid(v)) return;
          valueMap.set(tileName, v);
          if (v < min) min = v;
          if (v > max) max = v;
          anyValid = true;
        });
      });

      return {
        tileValueByName: valueMap,
        valueRange: anyValid ? { min, max } : null,
        hasData: anyValid,
      };
    }, [batch, target, metric, perWellTileOrder]);

    // Failed-well tile names so we can paint them as gaps explicitly
    const failedWellTileNames = useMemo(() => {
      const failedWells = new Set<string>();
      batch.WellCellPaintStats.forEach((w) => {
        if (w.Failure) failedWells.add(w.WellLocation);
      });
      const set = new Set<string>();
      tilePositions.forEach((tp) => {
        if (failedWells.has(tp.wellLocation)) set.add(tp.tileName);
      });
      return set;
    }, [batch, tilePositions]);

    // Tile rendering bounds (use absolute mm; fit into CELL inner box)
    const bounds = useMemo(() => {
      if (tilePositions.length === 0)
        return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
      const xs = tilePositions.map((p) => p.absoluteX);
      const ys = tilePositions.map((p) => p.absoluteY);
      return {
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys),
      };
    }, [tilePositions]);

    const innerW = CELL_W - CELL_PAD * 2;
    const innerH = CELL_H - CELL_PAD * 2 - 28; // reserve space for legend
    const spanX = bounds.maxX - bounds.minX || 1;
    const spanY = bounds.maxY - bounds.minY || 1;
    // Preserve aspect ratio of the flow cell layout
    const scale = Math.min(innerW / spanX, innerH / spanY);
    const renderW = spanX * scale;
    const renderH = spanY * scale;
    const offsetX = CELL_PAD + (innerW - renderW) / 2;
    const offsetY = CELL_PAD + (innerH - renderH) / 2;

    // Tile pixel size: each physical tile is ~1.8 x 2.6 mm. Render as a small
    // square sized to the layout scale, with a sane minimum so it stays visible.
    const tilePx = Math.max(2, scale * 2);

    if (!hasData) {
      return (
        <div
          style={{
            width: CELL_W,
            height: CELL_H,
            border: "1px dashed #ccc",
            borderRadius: 4,
            backgroundColor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#aaa",
            fontSize: 14,
          }}
          title={`${target} • ${metric}: not applicable`}
        >
          —
        </div>
      );
    }

    return (
      <div
        style={{
          width: CELL_W,
          height: CELL_H,
          border: "1px solid #ddd",
          borderRadius: 4,
          backgroundColor: "#fff",
          position: "relative",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            position: "relative",
            width: CELL_W,
            height: CELL_H - 28,
          }}
        >
          {tilePositions.map((tp) => {
            const isFailed = failedWellTileNames.has(tp.tileName);
            const value = tileValueByName.get(tp.tileName);
            const px = offsetX + (tp.absoluteX - bounds.minX) * scale;
            const py = offsetY + (tp.absoluteY - bounds.minY) * scale;
            const color = isFailed
              ? GAP_COLOR
              : value === undefined
              ? GAP_COLOR
              : colorForValue(
                  value,
                  valueRange?.min ?? 0,
                  valueRange?.max ?? 1
                );
            return (
              <div
                key={tp.tileName}
                style={{
                  position: "absolute",
                  left: px,
                  top: py,
                  width: tilePx,
                  height: tilePx,
                  backgroundColor: color,
                  borderRadius: 1,
                }}
                onMouseEnter={(e) => {
                  if (isFailed || value === undefined) {
                    setHover({
                      x: e.clientX,
                      y: e.clientY,
                      tileName: tp.tileName,
                      well: tp.wellLocation,
                      value: NaN,
                    });
                  } else {
                    setHover({
                      x: e.clientX,
                      y: e.clientY,
                      tileName: tp.tileName,
                      well: tp.wellLocation,
                      value,
                    });
                  }
                }}
                onMouseMove={(e) =>
                  setHover((h) =>
                    h ? { ...h, x: e.clientX, y: e.clientY } : h
                  )
                }
                onMouseLeave={() => setHover(null)}
              />
            );
          })}
        </div>

        {/* Mini color legend */}
        <div
          style={{
            position: "absolute",
            left: CELL_PAD,
            right: CELL_PAD,
            bottom: CELL_PAD,
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 10,
            color: "#666",
          }}
        >
          <span>{valueRange ? formatNum(valueRange.min) : "—"}</span>
          <div
            style={{
              flex: 1,
              height: 8,
              borderRadius: 2,
              background: `linear-gradient(to right, ${heatmapRamp.join(", ")})`,
              border: "1px solid #ddd",
            }}
          />
          <span>{valueRange ? formatNum(valueRange.max) : "—"}</span>
        </div>

        {hover && (
          <div
            style={{
              position: "fixed",
              left: hover.x + 12,
              top: hover.y + 12,
              backgroundColor: "rgba(0,0,0,0.85)",
              color: "white",
              padding: "6px 10px",
              borderRadius: 4,
              fontSize: 11,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 1000,
            }}
          >
            {hover.tileName} ({hover.well}) — {target} • {metric}:{" "}
            {isNaN(hover.value) ? "N/A" : hover.value.toPrecision(4)}
          </div>
        )}
      </div>
    );
  }
);

const formatNum = (v: number): string => {
  if (!isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs !== 0 && (abs < 1e-2 || abs >= 1e4)) return v.toExponential(1);
  return v.toPrecision(3);
};

export default CytoCellPaint;
